import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Target, Eye, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { companyInfo, teamMembers } from '../data/mock';
import ScrollReveal, { StaggerContainer, StaggerItem } from '../components/ui/ScrollReveal';

const AboutPage = () => {
  return (
    <div className="overflow-hidden bg-slate-950">
      {/* Hero Section */}
      <section className="relative pt-32 pb-16">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-teal-500/10 rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl">
            <ScrollReveal animation="fadeDown" delay={0.1}>
              <span className="text-emerald-400 font-semibold text-sm uppercase tracking-wider">About Us</span>
            </ScrollReveal>
            <ScrollReveal animation="fadeUp" delay={0.2}>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mt-4 mb-6 leading-tight">
                Building the Future of{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-teal-400">
                  Software
                </span>
              </h1>
            </ScrollReveal>
            <ScrollReveal animation="fadeUp" delay={0.3}>
              <p className="text-xl text-slate-400 leading-relaxed">
                Founded in 2025, Echo Media is a creative software company focused on building modern, high‑quality digital products. We combine design and technology to deliver solutions that are smart, scalable and memorable.
              </p>
            </ScrollReveal>
          </div>
        </div>
      </section>



      {/* Mission & Vision Section */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <StaggerContainer staggerDelay={0.2} className="grid md:grid-cols-2 gap-8">
            <StaggerItem animation="fadeRight">
              <Card className="border border-slate-800/50 bg-slate-900/50 rounded-2xl overflow-hidden hover:border-emerald-500/30 transition-all duration-500 h-full">
                <CardContent className="p-10">
                  <motion.div
                    className="w-14 h-14 rounded-2xl bg-emerald-500/10 flex items-center justify-center mb-6 border border-emerald-500/20"
                    animate={{
                      scale: [1, 1.1, 1],
                      rotate: [0, 5, -5, 0],
                      boxShadow: [
                        '0 0 0px rgba(16, 185, 129, 0)',
                        '0 0 20px rgba(16, 185, 129, 0.5)',
                        '0 0 0px rgba(16, 185, 129, 0)'
                      ]
                    }}
                    transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                  >
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
                    >
                      <Target className="w-7 h-7 text-emerald-400" />
                    </motion.div>
                  </motion.div>
                  <h3 className="text-2xl font-bold text-white mb-4">Our Mission</h3>
                  <p className="text-slate-400 leading-relaxed text-lg">
                    {companyInfo.mission}
                  </p>
                </CardContent>
              </Card>
            </StaggerItem>

            <StaggerItem animation="fadeLeft">
              <Card className="border border-slate-800/50 bg-slate-900/50 rounded-2xl overflow-hidden hover:border-teal-500/30 transition-all duration-500 h-full">
                <CardContent className="p-10">
                  <motion.div
                    className="w-14 h-14 rounded-2xl bg-teal-500/10 flex items-center justify-center mb-6 border border-teal-500/20"
                    animate={{
                      y: [0, -5, 0],
                      scale: [1, 1.05, 1],
                      boxShadow: [
                        '0 0 0px rgba(20, 184, 166, 0)',
                        '0 0 25px rgba(20, 184, 166, 0.6)',
                        '0 0 0px rgba(20, 184, 166, 0)'
                      ]
                    }}
                    transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }}
                  >
                    <motion.div
                      animate={{
                        scale: [1, 1.2, 1],
                        opacity: [1, 0.8, 1]
                      }}
                      transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
                    >
                      <Eye className="w-7 h-7 text-teal-400" />
                    </motion.div>
                  </motion.div>
                  <h3 className="text-2xl font-bold text-white mb-4">Our Vision</h3>
                  <p className="text-slate-400 leading-relaxed text-lg">
                    {companyInfo.vision}
                  </p>
                </CardContent>
              </Card>
            </StaggerItem>
          </StaggerContainer>
        </div>
      </section>

      {/* Company Story */}
      <section className="py-24 border-t border-slate-800/50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <ScrollReveal animation="fadeUp" className="max-w-3xl mx-auto text-center mb-16">
            <span className="text-emerald-400 font-semibold text-sm uppercase tracking-wider">Our Story</span>
            <h2 className="text-4xl font-bold text-white mt-4 mb-6">Why Echo Media?</h2>
          </ScrollReveal>

          <div className="max-w-4xl mx-auto">
            <StaggerContainer staggerDelay={0.2} className="space-y-6">
              {[
                "We design software and digital solutions that simplify your processes and help your business move faster, instead of adding extra layers of complexity.",
                "Our developers, designers, and strategists work as one team to understand your goals and deliver solutions that truly make a difference to your business.",
                "We focus on your unique challenges and build custom solutions that drive real results. For us, it's never just a project — it's the start of a lasting partnership."
              ].map((text, index) => (
                <StaggerItem key={index} animation="fadeUp">
                  <div className="flex gap-4">
                    <CheckCircle2 className="w-6 h-6 text-emerald-400 flex-shrink-0 mt-1" />
                    <p className="text-slate-400 text-lg leading-relaxed">{text}</p>
                  </div>
                </StaggerItem>
              ))}
            </StaggerContainer>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-24 border-t border-slate-800/50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <ScrollReveal animation="fadeUp" className="text-center mb-16">
            <span className="text-emerald-400 font-semibold text-sm uppercase tracking-wider">Our Team</span>
            <h2 className="text-4xl font-bold text-white mt-4 mb-6">Meet The Talent Behind Echo Media</h2>
            <p className="text-slate-400 max-w-3xl mx-auto text-lg">
              Our team is made up of highly capable developers, designers, and consultants who understand both technology and business. With a strong mix of skills and experience, we are confident in handling projects of any complexity and delivering results you can rely on.
            </p>
          </ScrollReveal>

          <StaggerContainer staggerDelay={0.15} className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {teamMembers.map((member) => (
              <StaggerItem key={member.id} animation="fadeUp">
                <Card className="group border border-slate-800/50 bg-slate-900/50 rounded-2xl overflow-hidden hover:border-emerald-500/30 transition-all duration-500 h-full">
                  <CardContent className="p-0">
                    <div className="aspect-square overflow-hidden">
                      <img
                        src={member.image}
                        alt={member.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-6">
                      <h3 className="text-lg font-semibold !text-white mb-1 group-hover:text-emerald-400 transition-colors">
                        {member.name}
                      </h3>
                      <p className="text-emerald-400 text-sm font-medium mb-3">
                        {member.role}
                      </p>
                      <p className="text-slate-400 text-sm leading-relaxed">
                        {member.bio}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* CTA Section */}
      <ScrollReveal animation="scaleUp">
        <section className="py-24">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center max-w-2xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                Want to Join Our Journey?
              </h2>
              <p className="text-slate-400 text-lg mb-8 leading-relaxed">
                Whether you're looking for a technology partner or want to join our team, we'd love to hear from you.
              </p>
              <Link to="/contact">
                <Button size="lg" className="bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold shadow-xl shadow-emerald-500/20 transition-all duration-300 text-base px-8 h-14 rounded-full">
                  Get in Touch
                  <ArrowRight size={18} className="ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </ScrollReveal>
    </div>
  );
};

export default AboutPage;
